import {
  IntegrationDataMap,
  MergeHRISIntegration,
  MergeIntegration,
  MergeIntegrationConfig
} from '@dadohr/shared/domainTypes/integrations/merge';
import { Dialog, DialogContent } from '@material-ui/core';
// In your React project folder, run:
// npm install --save @mergeapi/react-merge-link
import { useMergeLink } from '@mergeapi/react-merge-link';
import { Functionality } from '../..';
import { Loader } from '../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../components/SomethingWentWrong';
import { usePromise } from '../../../../../../hooks/usePromise';
import { useCurrentUser } from '../../../../../../services/currentUser';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties
} from '../common';
import { createIntegration, getLinkToken, useMergeConfig } from './service';

export const getMergeIntegrationLogo = (integration: MergeIntegration) => {
  if (integration === 'adp-workforce-now') {
    return '/images/integrations-logos/adp.svg';
  } else {
    return `/images/integrations-logos/merge/${integration}.png`;
  }
};

export const getMergeSmallIntegrationLogo = (integration: MergeIntegration) => {
  if (integration === 'adp-workforce-now') {
    return '/images/integrations-logos/adp.svg';
  } else {
    return `/images/integrations-logos/merge/small/ic_${integration}.png`;
  }
};

interface SetupDialogProps
  extends IntegrationSetupDialogWithConfigProperties<MergeIntegrationConfig> {
  orgId: string;
  hris: MergeHRISIntegration;
}

const IntermediaryLoaderDialog = () => {
  // This is shown while we're waiting for our own assets before we are able to display the Merge UI
  // The dimensions follow what Merge's UI will eventually be showing.
  // Ideally we would be able to adjust the backdrop color and the border radius of this Dialog
  // to make it look even more alike the Merge UI
  return (
    <Dialog open={true}>
      <div style={{ width: 375 }}>
        <Loader height={720} />
      </div>
    </Dialog>
  );
};

const MergeDialog = ({
  orgId,
  hris,
  linkToken,
  onSuccessfulIntegration,
  onFailure,
  onClose
}: {
  orgId: string;
  hris: MergeHRISIntegration;
  linkToken: string;
  onSuccessfulIntegration: () => void;
  onFailure: () => void;
  onClose: () => void;
}) => {
  const onSuccess = async (publicToken: string) => {
    try {
      await createIntegration(orgId, publicToken, hris);
      onDone(true, false);
    } catch (e: unknown) {
      onDone(false, true);
    }
  };

  const onDone = (success: boolean, failure: boolean) => {
    // The MergeUI sets these - and can then get stuck with these
    // values on close (especially when opening the MergeUI repeatedly in one session)
    // We're clearing the styles out ourselves. This is a bit dangerous, as e.g. MaterialUI
    // might also set these to something. As we do this before anything else it should be fine though -
    // even if MUI wants to set styles, they'd do it after us.
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    onClose();
    if (success) {
      onSuccessfulIntegration();
    }
    if (failure) {
      onFailure();
    }
  };
  const { open, isReady, error } = useMergeLink({
    linkToken,
    onSuccess,
    onExit: () => onDone(false, false)
  });
  if (error) {
    onDone(false, true);
    return null;
  }
  if (!isReady) {
    return <IntermediaryLoaderDialog />;
  }
  if (open && isReady) {
    open();
  }
  return null;
};

const SetupDialogInner = ({
  orgId,
  hris,
  onSuccessfulIntegration,
  onFailure,
  onClose
}: {
  orgId: string;
  hris: MergeHRISIntegration;
  onSuccessfulIntegration: () => void;
  onFailure: () => void;
  onClose: () => void;
}) => {
  const [linkTokenResponse, loading, error] = usePromise(() => {
    return getLinkToken(orgId, hris);
  }, [orgId, hris]);

  if (loading) {
    return <IntermediaryLoaderDialog />;
  }
  const linkToken = linkTokenResponse?.link_token;
  if (error || !linkToken) {
    return (
      <Dialog onClose={onClose} open={true}>
        <DialogContent>
          <SomethingWentWrong height={200} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <MergeDialog
      orgId={orgId}
      hris={hris}
      linkToken={linkToken}
      onSuccessfulIntegration={onSuccessfulIntegration}
      onFailure={onFailure}
      onClose={onClose}
    />
  );
};

const SetupDialog = ({
  config,
  orgId,
  open,
  onClose,
  onSuccessfulIntegration,
  hris,
  onFailure
}: SetupDialogProps) => {
  if (!open) {
    return null;
  }
  return (
    <SetupDialogInner
      orgId={orgId}
      hris={hris}
      onSuccessfulIntegration={onSuccessfulIntegration}
      onFailure={onFailure}
      onClose={onClose}
    />
  );
};

export const MergeSettingsButton = ({
  hris,
  functionalities,
  interactive
}: {
  hris: MergeHRISIntegration;
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { org } = useCurrentUser();
  const [config] = useMergeConfig(org.id);
  const cfg = config || null;
  const name = IntegrationDataMap[hris].name;
  const active = !!cfg?.data?.active && !!cfg.data.integrations[hris];

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={name}
      logoUrl={getMergeIntegrationLogo(hris)}
      active={active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          open={open}
          hris={hris}
          orgId={org.id}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
